import React from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Stack,
    Typography,
} from "@mui/material";

import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { getSurveyAvatarBadge, surveyStatusMessage } from "./SurveyFunctions";
import SurveyDetails from "./SurveyDetails";

import MSFAvatar from "../MSFAvatar";
import AlertMessage from "../../../Common/Alert/AlertMessage";
import ProgressBar from "../../../Common/ProgressBar/ProgressBar";

dayjs.extend(relativeTime);

function SurveyButton({ id, status, isFacilitator = false }) {
    let label = "View";
    let color = "neutral";
    let textColor = "rgba(0,0,0,0.7)";

    switch (status) {
        case "draft":
        case "open":
            if (!isFacilitator) {
                label = "Continue";
                color = "primary";
                textColor = "rgb(255.255.255)";
            }
            break;

        case "awaiting_approval":
            if (isFacilitator) {
                label = "Review";
                color = "primary";
                textColor = "rgb(255.255.255)";
            }
            break;

        case "review":
            if (isFacilitator) {
                label = "Review";
                color = "primary";
                textColor = "rgb(255.255.255)";
            }
            break;

        default:
            break;
    }

    return (
        <Button
            variant="contained"
            color={color}
            disableElevation={true}
            aria-label="Survey"
            component={Link}
            to={`/msf/surveys/${Number(id) || ""}`}
            endIcon={<KeyboardArrowRightOutlinedIcon />}
        >
            <Typography variant="span" sx={{ color: textColor }}>
                {label}
            </Typography>
        </Button>
    );
}

function SurveyListItem(props) {
    const survey = props.data;
    const user = props.user;

    const title = survey?.title ? survey.title : "360 feedback survey";
    const subTitle = survey?.type?.name
        ? survey.type.name
        : survey.resourceType;

    const isActive = survey?.status !== "completed" ? true : false;
    const isFacilitator = survey?.facilitator?.uuid === user?.uuid;
    const alert = surveyStatusMessage(survey?.status, isFacilitator);

    const minimum = survey?.result?.minimum ? survey.result.minimum : 0;
    const returned = survey?.result?.returned ? survey.result.returned : 0;

    const progressData = {
        title: "Responses",
        label: `/ ${minimum}`,
        total: returned,
        percentage:
            returned > 0 ? Math.min((returned / minimum) * 100, 100) : 0,
    };

    return (
        <Card className="card">
            <CardHeader
                disableTypography={true}
                avatar={
                    <MSFAvatar
                        type="survey"
                        badge={getSurveyAvatarBadge(
                            survey.status,
                            isFacilitator
                        )}
                    />
                }
                title={
                    <Typography component="h2" className="card-header-title">
                        {title}
                    </Typography>
                }
                subheader={
                    <Typography
                        component="span"
                        className="card-header-subtitle"
                    >
                        {subTitle}
                    </Typography>
                }
                action={
                    <Stack
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={2}
                        sx={{ padding: "8px" }}
                    >
                        <SurveyButton
                            id={survey.uuid}
                            status={survey.status}
                            isFacilitator={isFacilitator}
                        />
                    </Stack>
                }
            />
            <CardContent>
                <Box className="card-content">
                    <SurveyDetails
                        requester={survey?.requester}
                        areaOfWork={survey?.areaOfWork}
                        organisation={survey?.organisation}
                        facilitator={survey?.facilitator}
                        startDate={survey?.startDate}
                        endDate={survey?.endDate}
                        selfAssessment={survey?.selfAssessment}
                        report={survey?.report}
                        isFacilitator={isFacilitator}
                        isActive={isActive}
                    />
                </Box>
            </CardContent>

            {alert?.message && (
                <CardContent>
                    <Box className="card-content">
                        <AlertMessage
                            severity={alert.severity}
                            message={alert.message}
                        />
                    </Box>
                </CardContent>
            )}

            <CardContent>
                <Box className="card-content">
                    <ProgressBar data={progressData} />
                </Box>
            </CardContent>
        </Card>
    );
}

export default SurveyListItem;
